import appSettings from "@/appsettings.json";
import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import { useDeviceLayout } from "@/hooks/useDeviceLayout";
import ConductionAPI from "@/javascript/conduction-api";
import { isStripeConfigured, stripePromise } from "@/javascript/stripe";
import { Utils } from "@/javascript/utils";
import { Button, Card, Group, Loader, Stack, Stepper, Text } from "@mantine/core";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import type { StripeEmbeddedCheckoutOptions } from "@stripe/stripe-js";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type PurchaseStep = "select" | "checkout";

interface PurchaseSubmissionCreditsModalProps {
	opened: boolean;
	onClose: () => void;
	initialCredits?: number;
	authToken: string;
}

type SubmissionCreditsBundleConfig = {
	BundleId: string;
	Credits: number;
	Price?: string;
	PriceId?: string; // tolerate appsettings typo
};

const creditsPurchaseNotificationId = "submission-credits-purchase";

const isStripeCheckoutClientSecret = (s: string | null | undefined): boolean =>
	s != null && Boolean(s) && (s.startsWith("cs_test_") || s.startsWith("cs_live_"));

const tokenExpirationMonths: number = appSettings.SubmissionCredits?.ExpirationMonths ?? 3;

const rawBundles: SubmissionCreditsBundleConfig[] =
	(appSettings.SubmissionCredits?.Bundles as SubmissionCreditsBundleConfig[] | undefined) ?? [];

const normalizeBundles = (bundles: SubmissionCreditsBundleConfig[]): SubmissionCreditsBundleConfig[] => {
	return bundles
		.filter((b) => Boolean(b?.BundleId) && Number.isFinite(b?.Credits) && b.Credits > 0)
		.slice()
		.sort((a, b) => a.Credits - b.Credits);
};

const getBundlePriceNumber = (bundle: SubmissionCreditsBundleConfig): number | null => {
	const raw: string | undefined = bundle.Price ?? bundle.PriceId;
	if (!raw) return null;

	const cleaned: string = raw.replace("$", "").trim();
	const value: number = Number(cleaned);
	if (!Number.isFinite(value)) return null;
	return value;
};

const PurchaseSubmissionCreditsModal: React.FC<PurchaseSubmissionCreditsModalProps> = ({
	opened,
	onClose,
	initialCredits,
	authToken,
}) => {
	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AuthContext is undefined");
	}

	const bundles: SubmissionCreditsBundleConfig[] = useMemo(() => normalizeBundles(rawBundles), []);
	const creditOptions: number[] = useMemo(() => bundles.map((b) => b.Credits), [bundles]);

	const defaultCredits: number = creditOptions.length > 0 ? creditOptions[0] : 1;

	const getValidCredits = useCallback(
		(value?: number): number => {
			if (value === undefined || value === null) return defaultCredits;
			return creditOptions.includes(value) ? value : defaultCredits;
		},
		[creditOptions, defaultCredits]
	);

	const [step, setStep] = useState<PurchaseStep>("select");
	const [credits, setCredits] = useState<number>(getValidCredits(initialCredits));
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [clientSecret, setClientSecret] = useState<string | null>(null);

	const [awaitingCredits, setAwaitingCredits] = useState<boolean>(false);
	const [baselineRemainingCredits, setBaselineRemainingCredits] = useState<number>(0);

	const pollIntervalIdRef = useRef<number | null>(null);
	const pollAttemptsLeftRef = useRef<number>(0);
	const pollInFlightRef = useRef<boolean>(false);

	const userRef = useRef(auth.user);
	useEffect(() => {
		userRef.current = auth.user;
	}, [auth.user]);

	const clearPollingInterval = useCallback((): void => {
		if (pollIntervalIdRef.current !== null) {
			window.clearInterval(pollIntervalIdRef.current);
			pollIntervalIdRef.current = null;
		}
		pollAttemptsLeftRef.current = 0;
		pollInFlightRef.current = false;
	}, []);

	useEffect(() => {
		if (!opened) {
			// IMPORTANT: if the modal is being hidden but not unmounted (keepMounted),
			// force Stripe checkout subtree to disappear immediately.
			clearPollingInterval();
			setAwaitingCredits(false);
			setClientSecret(null);
			setStep("select");
			setError(null);
			return;
		}

		clearPollingInterval();
		setAwaitingCredits(false);
		setBaselineRemainingCredits(0);
		setStep("select");
		setClientSecret(null);
		setError(null);
		setLoading(false);
		setCredits(getValidCredits(initialCredits));
	}, [opened, initialCredits, clearPollingInterval, getValidCredits]);

	useEffect(() => {
		return () => {
			clearPollingInterval();
		};
	}, [clearPollingInterval]);

	const handleClose = useCallback((): void => {
		// Stop polling + ensure the embedded checkout is unrendered before closing.
		clearPollingInterval();
		setAwaitingCredits(false);
		setClientSecret(null);
		setStep("select");
		setError(null);
		onClose();
	}, [clearPollingInterval, onClose]);

	const selectedBundle: SubmissionCreditsBundleConfig | undefined = useMemo(() => {
		return bundles.find((b) => b.Credits === credits);
	}, [bundles, credits]);

	const selectedPriceText: string = useMemo(() => {
		if (!selectedBundle) return "—";
		const price: number | null = getBundlePriceNumber(selectedBundle);
		if (price === null) return "—";
		return `$${price}`;
	}, [selectedBundle]);

	const handleContinue = useCallback(async (): Promise<void> => {
		setLoading(true);
		setError(null);

		if (!isStripeConfigured) {
			setLoading(false);
			setError("Stripe checkout is unavailable right now. Please try again later.");
			return;
		}

		if (!selectedBundle) {
			setLoading(false);
			setError("Invalid bundle selection. Please try again.");
			return;
		}

		try {
			const before: number = userRef.current?.getRemainingActiveCredits() ?? 0;
			setBaselineRemainingCredits(before);

			// Call backend with bundleId (not credits)
			const response = await ConductionAPI.createSubmissionCreditsCheckoutSession(
				selectedBundle.BundleId,
				authToken
			);

			const secret: string | null = response?.clientSecret ?? null;

			if (!isStripeCheckoutClientSecret(secret)) {
				setClientSecret(null);
				setStep("checkout");
				setError("We couldn't start checkout. Please go back and try again.");
				return;
			}

			setClientSecret(secret);
			setStep("checkout");
		} catch (err) {
			const message: string =
				err instanceof Error && err.message ? err.message : "Checkout could not be started. Please try again.";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [authToken, selectedBundle]);

	const handleBack = useCallback((): void => {
		setStep("select");
		setClientSecret(null);
		setError(null);
		setAwaitingCredits(false);
		setBaselineRemainingCredits(0);
		clearPollingInterval();
	}, [clearPollingInterval]);

	const onCheckoutComplete = useCallback((): void => {
		Utils.displayNotification(
			"Payment successful",
			"Finalizing your submission credits. This may take a few seconds...",
			"blue",
			<IconCheck size={16} />,
			true,
			false,
			creditsPurchaseNotificationId
		);
		setAwaitingCredits(true);
	}, []);

	const runPoll = useCallback(async (): Promise<void> => {
		if (pollInFlightRef.current) return;

		pollInFlightRef.current = true;
		try {
			await auth.fetchProjectSubmissionTokens();

			const currentRemaining: number = userRef.current?.getRemainingActiveCredits() ?? 0;
			if (currentRemaining > baselineRemainingCredits) {
				Utils.updateNotification(
					creditsPurchaseNotificationId,
					"Credits added!",
					"Your submission credits are now available.",
					"green",
					<IconCheck size={16} />,
					true,
					5000
				);
				setAwaitingCredits(false);
				handleClose();
				return;
			}

			pollAttemptsLeftRef.current -= 1;
			if (pollAttemptsLeftRef.current <= 0) {
				Utils.updateNotification(
					creditsPurchaseNotificationId,
					"Payment received",
					"We're still processing your credits. If you don't see them soon, try refreshing the page.",
					"blue",
					<IconAlertCircle size={16} />,
					true,
					false
				);
				setAwaitingCredits(false);
				clearPollingInterval();
			}
		} catch (err) {
			console.error("Error polling for submission credits:", err);
		} finally {
			pollInFlightRef.current = false;
		}
	}, [auth, baselineRemainingCredits, clearPollingInterval, handleClose, handleClose]);

	useEffect(() => {
		if (!awaitingCredits) return;

		pollAttemptsLeftRef.current = 12;
		void runPoll();

		const intervalId: number = window.setInterval(() => {
			void runPoll();
		}, 2000);
		pollIntervalIdRef.current = intervalId;

		return () => {
			clearPollingInterval();
		};
	}, [awaitingCredits, clearPollingInterval, runPoll]);

	const checkoutOptions: StripeEmbeddedCheckoutOptions | undefined = useMemo(() => {
		if (!clientSecret) return undefined;
		return {
			clientSecret,
			onComplete: onCheckoutComplete,
		};
	}, [clientSecret, onCheckoutComplete]);

	const activeStep: number = step === "select" ? 0 : 1;

	const showStripeCheckout: boolean =
		opened &&
		step === "checkout" &&
		Boolean(checkoutOptions) &&
		isStripeCheckoutClientSecret(clientSecret) &&
		isStripeConfigured;

	const deviceLayout = useDeviceLayout();

	return (
		<ConductionModal
			title="Purchase Submission Credits"
			onClose={handleClose}
			opened={opened}
			size="xl"
			// IMPORTANT: ConductionModal should forward this to Mantine Modal keepMounted.
			// If ConductionModal doesn't accept this prop yet, add it there.
			keepMounted={false}
		>
			<Stack gap="md">
				{!deviceLayout.isPhoneOrSmaller && (
					<Stack gap="xs" align="center">
						<Stepper active={activeStep} allowNextStepsSelect={false} w="500px">
							<Stepper.Step label="Choose credits" />
							<Stepper.Step label="Checkout" />
						</Stepper>
					</Stack>
				)}

				{step === "select" && (
					<Stack gap="sm" mt="xl">
						<Group justify="center" gap="xs">
							{creditOptions.map((value: number) => (
								<Button
									key={value}
									variant={credits === value ? "filled" : "light"}
									onClick={() => setCredits(value)}
									disabled={loading}
								>
									{value} credit{value > 1 ? "s" : ""}
								</Button>
							))}
						</Group>

						<Card withBorder shadow="sm" radius="md">
							<Stack gap="xs">
								<Text fw={600}>
									{credits} submission credit{credits === 1 ? "" : "s"}
								</Text>
								<Text size="sm" c="dimmed">
									Each submission credit can be used to have one of your projects recorded by professional
									musicians
								</Text>
								<Text size="sm" c="dimmed">
									Credits expire after {tokenExpirationMonths} month{tokenExpirationMonths === 1 ? "" : "s"}
								</Text>
								<Text fw={600}>Total: {selectedPriceText}</Text>
							</Stack>
						</Card>

						{error && (
							<Text c="red" size="sm">
								{error}
							</Text>
						)}

						<Group justify="space-between">
							<Text size="sm" c="dimmed">
								Select how many credits you want to purchase.
							</Text>
							<Button onClick={() => void handleContinue()} loading={loading} disabled={!selectedBundle}>
								Continue to checkout
							</Button>
						</Group>
					</Stack>
				)}

				{step === "checkout" && (
					<Stack gap="sm" w="100%">
						<Group justify="space-between">
							<Button variant="subtle" onClick={handleBack}>
								Back
							</Button>
							<Button variant="light" onClick={handleClose}>
								Close
							</Button>
						</Group>

						{awaitingCredits && (
							<Group gap="xs">
								<Loader size="xs" />
								<Text size="sm" c="dimmed">
									Payment successful - finalizing your credits...
								</Text>
							</Group>
						)}

						{error && (
							<Text c="red" size="sm">
								{error}
							</Text>
						)}

						<div className="allow-vertical-scroll" style={{ maxHeight: "70vh", overflowY: "auto", width: "100%" }}>
							{showStripeCheckout && checkoutOptions ? (
								<EmbeddedCheckoutProvider
									// IMPORTANT: force a clean mount per Checkout Session
									key={clientSecret ?? "no-secret"}
									stripe={stripePromise}
									options={checkoutOptions}
								>
									<EmbeddedCheckout />
								</EmbeddedCheckoutProvider>
							) : (
								<Card withBorder shadow="sm" radius="md">
									<Stack gap="sm">
										<Text fw={700}>Checkout unavailable</Text>
										<Text size="sm" c="dimmed">
											We could not start checkout. Please go back and try again.
										</Text>
									</Stack>
								</Card>
							)}
						</div>
					</Stack>
				)}
			</Stack>
		</ConductionModal>
	);
};

export default PurchaseSubmissionCreditsModal;
